/**
 * 钉钉工具类 - 封装钉钉JSAPI相关功能
 */

declare global {
  interface Window {
    dd: any;
  }
}

/**
 * 检测是否是钉钉环境
 */
export function isDingTalkEnv(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  const isGdt = ua.includes('taurusapp'); // 排除专有钉钉
  const isDingTalk = ua.includes('dingtalk');
  return isDingTalk && !isGdt;
}

/**
 * 等待window.dd对象可用
 */
export function waitForDingTalkSDK(timeout = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    
    const checkDD = () => {
      if (window.dd) {
        console.log('✅ 钉钉SDK已就绪');
        resolve();
        return;
      }
      
      if (Date.now() - startTime > timeout) {
        reject(new Error('等待钉钉SDK超时'));
        return;
      }
      
      // 每50ms检查一次
      setTimeout(checkDD, 50);
    };
    
    checkDD();
  });
}

/**
 * 加载钉钉SDK
 */
export function loadDingTalkSDK(): Promise<void> {
  return new Promise((resolve, reject) => {
    const sdkUrl = 'https://g.alicdn.com/dingding/dingtalk-jsapi/2.10.3/dingtalk.open.js';
    
    // 检查是否已经加载
    if (window.dd) {
      console.log('✅ 钉钉SDK已存在');
      resolve();
      return;
    }

    // 检查script标签是否存在
    const existingScript = document.querySelector(`script[src="${sdkUrl}"]`);
    if (existingScript) {
      console.log('⏳ 钉钉SDK脚本已加载，等待初始化...');
      // script存在但window.dd还没准备好，等待它
      waitForDingTalkSDK()
        .then(resolve)
        .catch(reject);
      return;
    }

    console.log('📦 开始加载钉钉SDK...');
    const script = document.createElement('script');
    script.src = sdkUrl;
    script.async = true;
    
    script.onload = () => {
      console.log('📥 钉钉SDK脚本加载完成，等待初始化...');
      // 脚本加载完成，等待window.dd对象可用
      waitForDingTalkSDK()
        .then(() => {
          console.log('✅ 钉钉SDK完全就绪');
          resolve();
        })
        .catch(reject);
    };
    
    script.onerror = (error) => {
      console.error('❌ 钉钉SDK加载失败', error);
      reject(new Error('钉钉SDK加载失败'));
    };
    
    document.head.appendChild(script);
  });
}

/**
 * 钉钉导航栏右侧按钮配置接口
 */
export interface DingTalkSetRightParams {
  show?: boolean;      // 控制按钮显示， true 显示， false 隐藏， 默认true
  control?: boolean;   // 是否控制点击事件，true 控制，false 不控制， 默认false
  text?: string;       // 控制显示文本，空字符串表示显示默认文本
  onSuccess?: (result: any) => void;  // 成功回调
  onFail?: (error: any) => void;      // 失败回调
}

/**
 * 设置钉钉右侧标题栏按钮
 * @description 用于控制钉钉环境下右上角的三个点按钮
 */
export async function setDingTalkRight(params: DingTalkSetRightParams): Promise<void> {
  try {
    // 检查钉钉环境
    if (!isDingTalkEnv()) {
      console.log('⚠️ 非钉钉环境，跳过设置右侧标题栏');
      return;
    }

    // 确保SDK已加载
    if (!window.dd) {
      console.log('📦 钉钉SDK未加载，开始加载...');
      await loadDingTalkSDK();
    }

    // 检查API是否可用
    if (!window.dd?.biz?.navigation?.setRight) {
      console.error('❌ 钉钉导航栏API不可用');
      params.onFail?.({ message: '钉钉导航栏API不可用' });
      return;
    }

    const {
      show = false,
      control = false,
      text = '',
      onSuccess = () => {},
      onFail = () => {},
    } = params;

    console.log('🎨 设置钉钉右侧标题栏:', { show, control, text });

    window.dd.biz.navigation.setRight({
      show,
      control,
      text,
      onSuccess: (result: any) => {
        console.log('✅ 钉钉右侧标题栏设置成功', result);
        onSuccess(result);
      },
      onFail: (error: any) => {
        console.error('❌ 钉钉右侧标题栏设置失败', error);
        onFail(error);
      },
    }).catch((err: any) => {
      console.error('❌ 钉钉右侧标题栏设置异常', err);
      onFail(err);
    });
  } catch (error) {
    console.error('❌ 设置钉钉右侧标题栏异常', error);
    params.onFail?.(error);
  }
}

/**
 * 隐藏钉钉右上角的三个点
 * 这是一个便捷方法，用于快速隐藏右上角按钮
 */
export async function hideDingTalkRightButton(): Promise<void> {
  console.log('🚫 隐藏钉钉右上角三个点按钮');
  return setDingTalkRight({
    show: false,
    control: false,
  });
}

/**
 * 显示钉钉右上角的三个点
 * 这是一个便捷方法，用于显示右上角按钮
 */
export async function showDingTalkRightButton(
  text?: string,
  onClick?: () => void
): Promise<void> {
  console.log('👁️ 显示钉钉右上角三个点按钮', { text });
  return setDingTalkRight({
    show: true,
    control: !!onClick,
    text: text || '',
    onSuccess: onClick,
  });
}

